import { useEffect, useState } from 'react'
import { useMe, useUpdateMyDetails } from '@/hooks/queries'
import { notifyError } from '@/lib/notify'
import type { Media } from '@/models/media'
import { Avatar } from './ui/Avatar'
import { MediaPickerOverlay } from './MediaPickerOverlay'
import { ConnectedAccounts, ConnectedAccountsSkeleton } from './ConnectedAccounts'
import { SkeletonLine } from './ui/Skeleton'

interface MyProfileProps {
  onClose: () => void
}

/** Port of components/MyProfile.vue */
export function MyProfile({ onClose }: MyProfileProps) {
  const { data: me, isPending: meLoading } = useMe()
  const update = useUpdateMyDetails()

  const [selectingMedia, setSelectingMedia] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)

  const [username, setUsername] = useState('')
  const [nickname, setNickname] = useState('')
  const [email, setEmail] = useState('')
  const [profilePicture, setProfilePicture] = useState<Media | null>(null)

  // Seed the form once `me` arrives. The Vue version read the store in
  // `created()`, so it rendered empty fields whenever the overlay opened
  // before /me had resolved.
  useEffect(() => {
    if (!me) return
    setUsername(me.username ?? '')
    setNickname(me.nickname ?? '')
    setEmail(me.email ?? '')
    setProfilePicture(me.profile_picture)
  }, [me])

  const save = async () => {
    await update.mutateAsync({
      username,
      nickname,
      email,
      profile_picture_id: profilePicture?.id ?? null,
    })
    onClose()
  }

  return (
    <>
      {selectingMedia ? (
        <MediaPickerOverlay
          onClose={() => setSelectingMedia(false)}
          onSelect={(media) => {
            setProfilePicture(media)
            setSelectingMedia(false)
          }}
        />
      ) : null}

      <div className="my-profile">
        {meLoading ? (
          <ProfileSkeleton />
        ) : changingPassword ? (
          <ChangePassword onBack={() => setChangingPassword(false)} />
        ) : (
          <div className="change-profile">
            <div className="my-profile-details-row">
              <Avatar
                src={profilePicture?.src}
                className="my-profile-details-avatar"
                label="Your profile picture"
              />
              <div className="my-profile-details-text">
                <div className="my-profile-details-name">{me?.display_name}</div>
                <button
                  type="button"
                  className="my-profile-details-change cursor-pointer"
                  onClick={() => setSelectingMedia(true)}
                >
                  Change profile picture
                </button>
              </div>
            </div>

            {/* Labelled, not placeholder-only: with the fields filled in, the
                placeholders that named them were never visible. */}
            <label className="profile-field">
              <span className="field-name">Username</span>
              <input
                className="input-field"
                placeholder="Username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </label>
            <label className="profile-field">
              <span className="field-name">Display name</span>
              <input
                className="input-field"
                placeholder="Display name"
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
              />
            </label>
            <label className="profile-field">
              <span className="field-name">Email</span>
              <input
                className="input-field"
                placeholder="Email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>

            <div className="change-password-button-row">
              <button
                type="button"
                className="change-password-button"
                onClick={() => setChangingPassword(true)}
              >
                Change password
              </button>
            </div>

            <ConnectedAccounts />

            <div className="footer no-margin">
              <div className="cta-row">
                <button type="button" className="cta" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="cta next"
                  onClick={save}
                  disabled={update.isPending}
                >
                  {update.isPending ? <span className="spinner-inline" aria-hidden="true" /> : null}
                  {update.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/**
 * The original rendered this form with an empty `changePassword()` method, so
 * "Change" did nothing at all. There is no password endpoint in the ported API
 * surface either, so the form stays but says so rather than silently failing.
 */
function ChangePassword({ onBack }: { onBack: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')

  return (
    <div className="change-password">
      <input
        className="input-field"
        placeholder="Current Password"
        aria-label="Current password"
        type="password"
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
      />
      <input
        className="input-field"
        placeholder="New Password"
        aria-label="New password"
        type="password"
        value={next}
        onChange={(event) => setNext(event.target.value)}
      />

      <div className="footer no-margin">
        <div className="cta-row">
          <button type="button" className="cta" onClick={onBack}>
            Back
          </button>
          <button
            type="button"
            className="cta next"
            onClick={() => notifyError('Password changes are not available yet.')}
          >
            Change
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * The profile form's own markup while /me loads: avatar, name, the three
 * fields and the buttons, with the data greyed out and labels kept as text.
 */
function ProfileSkeleton() {
  return (
    <div className="change-profile is-skeleton" role="status" aria-live="polite">
      <span className="sr-only">Loading your profile</span>
      <div className="my-profile-details-row" aria-hidden="true">
        <div className="my-profile-details-avatar skeleton" />
        <div className="my-profile-details-text">
          <div className="my-profile-details-name">
            <SkeletonLine width="7em" />
          </div>
          <span className="my-profile-details-change">Change profile picture</span>
        </div>
      </div>
      {['Username', 'Display name', 'Email'].map((label) => (
        <div className="profile-field" key={label} aria-hidden="true">
          <span className="field-name">{label}</span>
          <div className="input-field skeleton-field">
            <SkeletonLine width="40%" />
          </div>
        </div>
      ))}
      <div className="change-password-button-row" aria-hidden="true">
        <span className="change-password-button">Change password</span>
      </div>
      <ConnectedAccountsSkeleton />
      <div className="footer no-margin" aria-hidden="true">
        <div className="cta-row">
          <span className="cta">Cancel</span>
          <span className="cta next">Save</span>
        </div>
      </div>
    </div>
  )
}
